// Static data: checklists, timeline, vendors, inclusions options.

// Single source of truth for prep guide categories — used by:
//   - PrepGuide.jsx (rendering order + add-task UI)
//   - AdminContent.jsx (Prep Guide editor tabs)
//   - AdminSellerDetail.jsx (Custom Tasks dropdown)
// Keep this aligned with backend Literal in server.py (CustomTask + SellerTaskCreate).
export const PREP_CATEGORIES = [
  { id: "repairs", label: "Repairs" },
  { id: "purges", label: "Purges" },
  { id: "photo_prep", label: "Photo and Showings Prep" },
  { id: "exterior", label: "Exterior" },
  { id: "kitchen", label: "Kitchen" },
  { id: "living_room", label: "Living Room" },
  { id: "bedrooms", label: "Bedrooms" },
  { id: "bathrooms", label: "Bathrooms" },
  { id: "miscellaneous", label: "Miscellaneous" },
];

export const REPAIRS_ITEMS = [
  { id: "r1", label: "Patch nail holes and repaint scuffed walls" },
  { id: "r2", label: "Replace burnt-out bulbs (match color temperature)" },
  { id: "r3", label: "Fix leaky faucets and running toilets" },
  { id: "r4", label: "Re-caulk tubs, showers, and kitchen sink" },
  { id: "r5", label: "Tighten loose door knobs, cabinet pulls, hinges" },
  { id: "r6", label: "Oil squeaky doors and hinges" },
  { id: "r7", label: "Replace cracked outlet / switch plates" },
  { id: "r8", label: "Touch up baseboards and trim" },
  { id: "r9", label: "Replace HVAC filter; service unit if overdue" },
];

export const PURGES_ITEMS = [
  { id: "p1", label: "Pack away personal photos and memorabilia" },
  { id: "p2", label: "Clear 50% of items from closets and pantry shelves" },
  { id: "p3", label: "Remove small appliances from kitchen counters" },
  { id: "p4", label: "Donate or store excess furniture that blocks flow" },
  { id: "p5", label: "Purge bathroom counters — store toiletries" },
  { id: "p6", label: "Declutter garage (buyers want to see storage)" },
  { id: "p7", label: "Remove magnets, papers, art from the fridge" },
  { id: "p8", label: "Pack away valuables, prescriptions, and firearms" },
];

export const PHOTO_PREP_ITEMS = [
  { id: "ph1", label: "Mow, edge, and sweep walkways" },
  { id: "ph2", label: "Park all cars down the street" },
  { id: "ph3", label: "Open every blind and turn on every light" },
  { id: "ph4", label: "Hide trash cans, pet bowls, and pet toys" },
  { id: "ph5", label: "Make beds with neutral, clean linens" },
  { id: "ph6", label: "Clear countertops and polish surfaces" },
  { id: "ph7", label: "Remove cords, phone chargers, and remotes from view" },
  { id: "ph8", label: "Fresh flowers or green plants on main surfaces" },
  { id: "ph9", label: "Replace any mismatched hand towels" },
];

export const EXTERIOR_ITEMS = [
  { id: "ex1", label: "Mow, edge, and weed garden beds" },
  { id: "ex2", label: "Power-wash siding, walkways, and driveway" },
  { id: "ex3", label: "Paint or stain the front door; polish house numbers" },
  { id: "ex4", label: "Add a fresh welcome mat and potted plants by the entry" },
  { id: "ex5", label: "Clean gutters and downspouts" },
  { id: "ex6", label: "Wash exterior windows inside and out" },
  { id: "ex7", label: "Stow hoses, toys, and tools out of sight" },
  { id: "ex8", label: "Make sure porch lights, house numbers, and doorbell all work" },
  { id: "ex9", label: "Remove temporary Christmas / seasonal lights and decorations" },
];

export const KITCHEN_ITEMS = [
  { id: "kt1", label: "Clear every counter — store small appliances" },
  { id: "kt2", label: "Empty and wipe the fridge (interior shows on photos)" },
  { id: "kt3", label: "Scrub stovetop, oven, microwave, and range hood" },
  { id: "kt4", label: "Polish stainless or wipe down appliance fronts" },
  { id: "kt5", label: "Organize pantry — face labels forward" },
  { id: "kt6", label: "Empty and wipe under-sink cabinet" },
  { id: "kt7", label: "Replace the dish towel; hide the sponge and soap bottle" },
  { id: "kt8", label: "Set a clean bowl of fruit or a small plant as a styling moment" },
];

export const LIVING_ROOM_ITEMS = [
  { id: "lr1", label: "Remove 30–40% of furniture so the space reads open" },
  { id: "lr2", label: "Vacuum and spot-clean upholstery" },
  { id: "lr3", label: "Fluff pillows, straighten throws" },
  { id: "lr4", label: "Clear coffee table except 2–3 styled items" },
  { id: "lr5", label: "Hide cords, chargers, and remotes" },
  { id: "lr6", label: "Dust blades of ceiling fan and top of TV" },
  { id: "lr7", label: "Swap family photos for neutral art if possible" },
  { id: "lr8", label: "Open blinds/curtains fully during showings" },
];

export const BEDROOMS_ITEMS = [
  { id: "bd1", label: "Make beds with white or neutral linens" },
  { id: "bd2", label: "Clear nightstands to a lamp + one styled item" },
  { id: "bd3", label: "Organize closets — 50% empty is the goal" },
  { id: "bd4", label: "Remove all personal photos and mementos" },
  { id: "bd5", label: "Vacuum under beds; tuck dust ruffles" },
  { id: "bd6", label: "Dust ceiling fan and baseboards" },
  { id: "bd7", label: "Open blinds fully; match lamp shade heights" },
  { id: "bd8", label: "Stow any exercise equipment or laundry baskets" },
];

export const BATHROOMS_ITEMS = [
  { id: "ba1", label: "Scrub grout, tile, tub, and shower glass" },
  { id: "ba2", label: "Polish mirrors, faucets, and chrome" },
  { id: "ba3", label: "Hang fresh, matching hand towels (not the ones you use daily)" },
  { id: "ba4", label: "Replace any stained or dingy bath mat" },
  { id: "ba5", label: "Empty countertops — store toiletries in baskets under sink" },
  { id: "ba6", label: "Replace toilet paper with a fresh roll, toilet lid closed" },
  { id: "ba7", label: "Wipe out the trash can or hide it during showings" },
  { id: "ba8", label: "Light a subtle neutral candle 30 min before showings" },
];

export const MISCELLANEOUS_ITEMS = [
  { id: "mc1", label: "Organize the laundry room — detergent bottles hidden" },
  { id: "mc2", label: "Clear and sweep the garage floor; donate what you don't need" },
  { id: "mc3", label: "Tidy storage closets — use matching bins if visible" },
  { id: "mc4", label: "Test every smoke detector and replace dead batteries" },
  { id: "mc5", label: "Clean HVAC return vent and replace the filter" },
  { id: "mc6", label: "Touch up scuff marks on staircase walls and trim" },
  { id: "mc7", label: "Place doormats at both front and back entries" },
  { id: "mc8", label: "Create a 'last 10 minutes' pre-showing checklist and post it inside a cabinet" },
];

export const TIMELINE_STAGES = [
  {
    id: "strategy",
    title: "Strategy Meeting",
    blurb: "We walk the home together, discuss pricing, marketing, and timeline.",
    actions: ["Identify must-do repairs", "Review comps & set price range", "Lock in listing launch date"],
  },
  {
    id: "prep",
    title: "Prep & Staging",
    blurb: "Declutter, repair, and stage so the home photographs beautifully.",
    actions: ["Complete the Prep Guide checklists", "Schedule staging consultation", "Deep clean before photos"],
  },
  {
    id: "photo",
    title: "Photo & Video",
    blurb: "Professional photos, video, and floor plan are captured.",
    actions: ["Morning-of photo prep checklist", "All lights on, blinds open", "Pets and cars out"],
  },
  {
    id: "launch",
    title: "Listing Launch",
    blurb: "Your home goes live on MLS and syndicates everywhere buyers look.",
    actions: ["MLS live", "Social & email campaigns", "Open house scheduled"],
  },
  {
    id: "showings",
    title: "Showings & Offers",
    blurb: "Buyers tour the home. We review offers together and negotiate.",
    actions: ["Keep home show-ready", "Review each offer carefully", "Run the Seller Net Sheet"],
  },
  {
    id: "pending",
    title: "Under Contract",
    blurb: "Inspections, appraisal, and financing milestones are managed.",
    actions: ["Inspection response", "Appraisal scheduled", "Clear to close"],
  },
  {
    id: "closing",
    title: "Closing Day",
    blurb: "You sign, funds disburse, and keys change hands.",
    actions: ["Bring valid photo ID", "Leave home broom-clean", "Transfer utilities"],
  },
];

export const INCLUSION_OPTIONS = [
  "Alarm System",
  "Basketball Standard",
  "Ceiling Fan",
  "Compactor",
  "Dishwasher: Portable",
  "Dog Run",
  "Dryer",
  "Electric Air Cleaner",
  "Fireplace Equipment",
  "Fireplace Insert",
  "Freezer",
  "Gas Grill / BBQ",
  "Gazebo",
  "Hot Tub",
  "Humidifier",
  "Microwave",
  "Play Gym",
  "Projector",
  "Range",
  "Range Hood",
  "Refrigerator",
  "Satellite Dish",
  "Satellite Equipment",
  "Smart Thermostat(s)",
  "Storage Shed(s)",
  "Swing Set",
  "Trampoline",
  "TV Antenna",
  "Video Camera(s)",
  "Video Door Bell(s)",
  "Washer",
  "Water Softener: Own",
  "Water Softener: Rent",
  "Window Coverings",
  "Woodstove",
  "Workbench",
];

// Plain-English hints for the items buyers and sellers most often argue about.
// Shown as a small info tooltip on the Inclusions & Exclusions form.
export const INCLUSION_HINTS = {
  "Alarm System": "If your system is leased or monitored, confirm whether the contract transfers to the buyer — otherwise it usually goes with you.",
  "Dishwasher: Portable": "Built-in dishwashers are assumed to stay. A portable (rolling) unit leaves with you unless you list it as included.",
  "Dryer": "Not a fixture — specify whether it stays. Stacked/plumbed units often do.",
  "Fireplace Equipment": "Screen, tools, andirons, grates. Decorative only — easy to overlook.",
  "Fireplace Insert": "The stove-style insert itself. If hard-plumbed or vented, usually stays; confirm anyway.",
  "Gas Grill / BBQ": "Built-in grills stay by default. A free-standing BBQ leaves unless listed.",
  "Hot Tub": "If hard-wired or plumbed in, typically stays. Plug-and-play portable tubs often leave.",
  "Humidifier": "Whole-house units attached to the HVAC stay. Portable humidifiers leave.",
  "Microwave": "Built-in/over-the-range microwaves stay. Counter-top units leave unless listed.",
  "Range": "Built-in and slide-in ranges stay. Free-standing ranges usually do too — but confirm.",
  "Range Hood": "Ducted or hard-wired hoods stay with the home.",
  "Refrigerator": "Built-in (cabinet-integrated) fridges stay. Free-standing fridges are often excluded — be explicit.",
  "Satellite Dish": "The dish on the roof typically stays. The receiver/box may be leased — confirm with your provider.",
  "Satellite Equipment": "Leased receivers and remotes usually return to the provider. Owned equipment can stay.",
  "Smart Thermostat(s)": "These replace the original thermostat and typically stay. If you want to keep it, note it here and leave a basic replacement.",
  "Storage Shed(s)": "Anchored or bolted-down sheds stay. A shed on skids that can be moved may be excluded if you list it.",
  "Swing Set": "Anchored/concreted sets stay. Free-standing sets can go with you if listed.",
  "Trampoline": "Free-standing — usually leaves with you unless you list it as included.",
  "TV Antenna": "Roof-mounted antennas stay with the home.",
  "Video Camera(s)": "Hard-wired cameras stay. Battery/plug-in cameras (Ring, Arlo, Wyze) can go with you — note which.",
  "Video Door Bell(s)": "Hard-wired doorbells stay. If you want to keep yours, note it and leave a basic replacement.",
  "Washer": "Not a fixture — specify whether it stays.",
  "Water Softener: Own": "Owned softener stays with the home.",
  "Water Softener: Rent": "Rented softener typically returns to the leasing company — list it as excluded.",
  "Window Coverings": "Blinds & shades attached to windows usually stay. Curtains, drapes, and rods are personal property unless listed.",
  "Woodstove": "Installed and vented stoves stay with the home.",
  "Workbench": "Bolted-down workbenches stay. A free-standing bench can go with you if listed.",
};

export const VENDORS = [
  { category: "Cleaning", name: "Sparkle & Shine Cleaning", phone: "(555) 010-2211", email: "hello@sparklecleaning.co" },
  { category: "Staging", name: "Hearthstone Staging Co.", phone: "(555) 010-4488", email: "book@hearthstonestage.com" },
  { category: "Handyman", name: "Fix-It Friends", phone: "(555) 010-5521", email: "jobs@fixitfriends.co" },
  { category: "Inspections", name: "Cornerstone Home Inspection", phone: "(555) 010-7712", email: "schedule@cornerstonehi.com" },
  { category: "Moving", name: "Two Pines Movers", phone: "(555) 010-9012", email: "quote@twopinesmovers.com" },
  { category: "Landscaping", name: "Greenline Lawn Care", phone: "(555) 010-3344", email: "info@greenlinelawn.com" },
  { category: "Painting", name: "North & Co. Painters", phone: "(555) 010-6677", email: "jobs@northcopainters.com" },
  { category: "Photography", name: "Loft Media Real Estate", phone: "(555) 010-8899", email: "shoot@loftmedia.com" },
];

export const CLOSING_CHECKLIST = [
  { id: "c1", label: "Bring valid government-issued photo ID to signing" },
  { id: "c2", label: "Confirm wire instructions directly with title company by phone" },
  { id: "c3", label: "Leave home in broom-clean condition" },
  { id: "c4", label: "Remove all personal items (including attic & crawlspace)" },
  { id: "c5", label: "Leave appliance manuals, garage remotes, spare keys on counter" },
  { id: "c6", label: "Cancel or transfer utilities (electric, gas, water, internet, trash)" },
  { id: "c7", label: "Update address with USPS, bank, employer, DMV" },
  { id: "c8", label: "Take final meter readings" },
  { id: "c9", label: "Forward mail" },
];


// ==========================================================================
// BUYER-SIDE DEFAULTS
// ==========================================================================
// When a client's active_side is "buy", the seller-portal pages swap in
// these buy-flavored defaults for timeline / date_fields / closing.
// Editable per-tenant via /admin/content in a future iteration; today they
// come straight from these constants.

export const BUYER_TIMELINE_STAGES = [
  {
    id: "preapproval",
    step: 1,
    title: "Get Pre-Approved",
    summary: "Connect with a trusted lender to know your buying power before you fall in love with a home.",
    details: "Pre-approval shows sellers you're serious. Gather pay stubs, W-2s, tax returns, and bank statements. Your lender will run credit and issue a letter typically valid 60–90 days.",
  },
  {
    id: "wishlist",
    step: 2,
    title: "Define Your Wishlist",
    summary: "Clarify must-haves, nice-to-haves, and dealbreakers. We'll match this to active listings.",
    details: "Think about commute, schools, lot size, layout, and long-term plans. Submit your Preferences form so we can curate showings that respect your time.",
  },
  {
    id: "tour",
    step: 3,
    title: "Tour & Evaluate",
    summary: "Visit homes that match your wishlist. Take notes — they blur together fast.",
    details: "I'll send a curated list each week. We'll tour 3–5 at a time and debrief each home. Pay attention to neighborhood feel, light, and any signs of deferred maintenance.",
  },
  {
    id: "offer",
    step: 4,
    title: "Make an Offer",
    summary: "We craft a competitive, strategic offer with your timeline and protections in mind.",
    details: "We discuss price, earnest money, contingencies (financing, inspection, appraisal), and closing timeline. I'll handle negotiation and paperwork.",
  },
  {
    id: "closing",
    step: 5,
    title: "Under Contract → Closing",
    summary: "Inspection, appraisal, financing, walkthrough, keys. I'll guide you through every date.",
    details: "Once accepted, your timeline tab will populate with key dates. Stay responsive to your lender — and don't make big purchases until after closing.",
  },
];

export const BUYER_DATE_FIELDS = [
  { key: "preapproval_letter", label: "Pre-approval letter received" },
  { key: "offer_accepted", label: "Offer accepted (mutual acceptance)" },
  { key: "earnest_money_wired", label: "Earnest money wired" },
  { key: "inspection", label: "Home inspection" },
  { key: "inspection_response_deadline", label: "Inspection response deadline" },
  { key: "appraisal", label: "Appraisal" },
  { key: "finance_contingency_deadline", label: "Financing contingency deadline" },
  { key: "final_walkthrough", label: "Final walk-through" },
  { key: "closing_date", label: "Closing day" },
];

export const BUYER_CLOSING_CHECKLIST = [
  { id: "bc1", label: "Bring valid government-issued photo ID to signing" },
  { id: "bc2", label: "Confirm wire instructions directly with the title company by phone (never trust email alone)" },
  { id: "bc3", label: "Wire closing funds early — don't wait until the morning-of" },
  { id: "bc4", label: "Do the final walk-through the day before closing" },
  { id: "bc5", label: "Set up homeowner's insurance to start on closing day" },
  { id: "bc6", label: "Set up utilities (electric, gas, water, internet, trash) in your name" },
  { id: "bc7", label: "Update address with USPS, bank, employer, DMV" },
  { id: "bc8", label: "Change locks or rekey soon after moving in" },
];
